import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { colors } from '@lib/colors/colors'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Doughnut } from 'react-chartjs-2'

import { romanize } from '@lib/utilities/number/romanize'
import { LegendItem } from './LegendItem'

// Registration is necessary to display the various plugins
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels)

const doughnutDisplayOptions = {
  cutout: '80%',
  plugins: {
    datalabels: {
      align: 'center',
      color: 'black',
      font: { size: 16, weight: 'bold' },
      formatter(_value: string, _context: any) {
        return ''
      },
    },
    legend: false,
    tooltip: {
      callbacks: {
        label: (_context: any) => {
          return ''
        },
        title: (_context: any) => {
          const l = _context[0].dataset.label || _context[0].label || ''
          return l
        },
      },
    },
  },
} as any // FIXME: this casting could cause problems later on!

export type ArmGroupForTreatmentDoughnut = {
  color: string
  name: string
  type: 'Active Control' | 'Experimental Treatment' | 'Placebo Group'
}

export const TreatmentDoughnut = ({
  armGroups,
  layout = 'wide',
  selectedGroupIndex,
  treatmentNameInLegend = false,
}: {
  armGroups: ArmGroupForTreatmentDoughnut[]
  layout?: 'narrow' | 'wide'
  selectedGroupIndex?: number
  treatmentNameInLegend?: boolean
}) => {
  const data = {
    datasets: [
      {
        backgroundColor: armGroups.map((g) => g.color),
        borderColor: 'white',
        borderWidth: 6,
        data: armGroups.map((_g) => 1),
      },
    ],
    labels: armGroups.map((g) => g.name),
  }

  const selectedGroup =
    selectedGroupIndex !== -1 ? armGroups[selectedGroupIndex!] : undefined

  if (selectedGroup) {
    data.datasets[0]!.backgroundColor = armGroups.map((g) =>
      g === selectedGroup ? g.color : colors.neutral200,
    )
  }

  return (
    <div className='flex flex-wrap items-center justify-center gap-8'>
      <div className='relative'>
        <Doughnut data={data} options={doughnutDisplayOptions} />
        <div className='absolute left-0 top-1/3 flex h-[100px] w-full flex-col items-center justify-center text-center'>
          {selectedGroup ? (
            <>
              <Text
                className='line-clamp-2 w-[78%]'
                styleName='h4'
                value={selectedGroup.name}
              />
              <Text
                className='w-[78%] text-neutral600'
                styleName='p-small'
                value={selectedGroup.type}
              />
            </>
          ) : (
            <>
              <Text styleName='h1' value={data.labels.length} />
              <Text value='Treatment groups' />
            </>
          )}
        </div>
      </div>
      <DoughnutLegend
        armGroups={armGroups}
        layout={layout}
        treatmentNameInLegend={treatmentNameInLegend}
      />
    </div>
  )
}

const DoughnutLegend = ({
  armGroups,
  layout = 'wide',
  treatmentNameInLegend = false,
}: {
  armGroups: ArmGroupForTreatmentDoughnut[]
  layout?: 'narrow' | 'wide'
  treatmentNameInLegend?: boolean
}) => {
  const countsByGroupType = armGroups.reduce(
    (counts, group) => {
      counts[group.type] = (counts[group.type] ?? 0) + 1
      return counts
    },
    {} as { [type: string]: number },
  )

  const firstTreatmentInGroupType = armGroups.reduce(
    (names, group, index) => {
      if (!names[group.type]) {
        names[group.type] = `Group ${romanize(index + 1)}: ${group.name}`
      }
      return names
    },
    {} as { [type: string]: string },
  )

  const activeControlCount = countsByGroupType['Active Control'] ?? 0
  const experimentalTreatmentCount =
    countsByGroupType['Experimental Treatment'] ?? 0
  const placeboGroupCount = countsByGroupType['Placebo Group'] ?? 0

  const legendStyles =
    layout === 'wide' ? 'md:w-1/3 md:flex-col md:items-start' : ''
  const legendItemStyles = 'line-clamp-1 md:line-clamp-2'

  // If an arm group has multiple treatments, we should alwasy display the group type in the legend
  const doesGroupHaveSingleTreatment = [
    experimentalTreatmentCount,
    activeControlCount,
    placeboGroupCount,
  ].every((count) => count <= 1)

  return (
    <div
      className={classMerge(
        `flex w-full flex-wrap items-center justify-center gap-2`,
        legendStyles,
      )}
    >
      {treatmentNameInLegend && doesGroupHaveSingleTreatment ? (
        <>
          {firstTreatmentInGroupType['Experimental Treatment'] && (
            <LegendItem
              className={legendItemStyles}
              color={colors.blue500}
              label={firstTreatmentInGroupType['Experimental Treatment']}
            />
          )}
          {firstTreatmentInGroupType['Active Control'] && (
            <LegendItem
              className={legendItemStyles}
              color={colors.success500}
              label={firstTreatmentInGroupType['Active Control']}
            />
          )}
          {firstTreatmentInGroupType['Placebo Group'] && (
            <LegendItem
              className={legendItemStyles}
              color={colors.neutral400}
              label={firstTreatmentInGroupType['Placebo Group']}
            />
          )}
        </>
      ) : (
        <>
          {experimentalTreatmentCount > 0 && (
            <LegendItem color={colors.blue500} label='Experimental Treatment' />
          )}
          {activeControlCount > 0 && (
            <LegendItem color={colors.success500} label='Active Control' />
          )}
          {placeboGroupCount > 0 && (
            <LegendItem color={colors.neutral400} label='Placebo Group' />
          )}
        </>
      )}
    </div>
  )
}
